import React from 'react';
import {
  TrendingUp, IndianRupee, BarChart3, Target, LineChart,
  Zap, Activity, Clock, Shield, Users, Globe,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const EnergyTradingOptimizationPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Energy Trading',
      title: (
        <>
          Intelligent energy trading <em>optimization platform</em>
        </>
      ),
      lede: 'Maximize your energy trading revenue with our advanced optimization platform. Leverage AI-driven market insights, price forecasting, and automated trading strategies to stay ahead in energy markets.',
      image: '/assets/img/general/energy-2302001_1280.jpg',
      imageAlt: 'Energy transmission infrastructure',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '30%', label: 'Revenue increase' },
        { value: 'Real-time', label: 'Market analysis' },
        { value: '95%', label: 'Forecast accuracy' },
        { value: '24/7', label: 'Trading support' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A complete trading solution',
      lede: 'Our energy trading optimization platform provides all the tools and insights you need to succeed in energy markets.',
      body: [
        'Forecasting, bidding strategy, risk exposure and settlement performance are handled as one workflow, so trading decisions are made against the same data the desk is measured on.',
      ],
      checklist: [
        'Real-time energy market monitoring and analysis',
        'Advanced price forecasting and prediction models',
        'Automated trading strategy optimization',
        'Portfolio management and diversification',
        'Risk assessment and mitigation tools',
        'Market opportunity identification',
        'Regulatory compliance management',
        'Comprehensive trading performance analytics',
      ],
      image: '/assets/img/general/steptodown.com799996.jpg',
      imageAlt: 'Renewable energy generation site',
      imageCaption: 'Optimize energy trading with AI-driven market insights and real-time analytics',
    }}
    capabilities={{
      eyebrow: 'Trading features',
      title: 'Advanced trading capabilities',
      lede: 'Comprehensive tools for energy market optimization.',
      items: [
        {
          icon: TrendingUp,
          title: 'Market analysis',
          description: 'Real-time energy market analysis with predictive insights for optimal trading decisions.',
        },
        {
          icon: BarChart3,
          title: 'Price forecasting',
          description: 'Advanced algorithms to forecast energy prices and identify trading opportunities.',
        },
        {
          icon: Target,
          title: 'Strategy optimization',
          description: 'Optimize trading strategies to maximize revenue and minimize market risks.',
        },
        {
          icon: LineChart,
          title: 'Performance tracking',
          description: 'Track and analyze trading performance with comprehensive metrics and reporting.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Trading markets',
      title: 'Comprehensive market coverage',
      lede: 'Optimize trading across all energy market segments.',
      items: [
        {
          icon: Clock,
          title: 'Day-ahead trading',
          description: 'Optimize day-ahead market participation with accurate forecasting and bidding strategies.',
        },
        {
          icon: Activity,
          title: 'Intraday trading',
          description: 'Capitalize on intraday market opportunities with real-time optimization.',
        },
        {
          icon: Users,
          title: 'Bilateral contracts',
          description: 'Optimize long-term bilateral contract negotiations and management.',
        },
        {
          icon: Globe,
          title: 'Grid services',
          description: 'Maximize revenue from ancillary services and grid balancing markets.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Maximize trading performance',
      lede: 'Achieve superior results through intelligent trading optimization.',
      items: [
        {
          icon: IndianRupee,
          title: 'Maximize revenue',
          description: 'Increase trading revenue by 15-30% through optimized market participation.',
        },
        {
          icon: Shield,
          title: 'Risk management',
          description: 'Minimize market exposure and trading risks with intelligent risk management.',
        },
        {
          icon: Zap,
          title: 'Real-time trading',
          description: 'Execute trades instantly based on real-time market conditions and opportunities.',
        },
        {
          icon: BarChart3,
          title: 'Market insights',
          description: 'Gain deep insights into market trends and patterns for better decision-making.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize trading?',
      body: 'Start maximizing your energy trading revenue with intelligent optimization.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default EnergyTradingOptimizationPage;
